/*
 * Graph attributes usage:
 * - data - data shown in graph
 *      data = {
 *               graph1: [
 *                         {x: time, y: value}
 *                       ]
 *               graph2: [
 *                         {x: time, y: value}
 *                       ]
 *               graph3: ....
 *             }
 * - areaChart - array with boolean values, for which of the graphs will be displayed as area chart, otherwise it will be sparkline
 * - colors - array of objects, corresponding to multiple values, containing fillColor and strokeColor, {fillColor: 'color', strokeColor: 'color'}, if not area chart stroke color not necessary
 * - timeStep - number of timeUnits passed between two consecutive ticks on x axis
 * - timeUnit - time unit for x axis (seconds, minutes...)
 * - timeFormat -  format for the time axis (optional)
 * - valueUnit - string representing a unit to be shown beside y value, in tooltip
 * - maxValue - max value for the graph y axis, if not provided it will be scaled to highest value in data
 * - period - time span of the x axis, if not provided it will be calculated with time span of the data
 * - startTime - if set, specifies where domains starts, domain: (startTime - period, startTime)
 * - graphTitle - title above the graph
 * - timeTick - boolean, if true graph will tick in time, otherwise it will be stationary
 * - yAxisNumber - number of ticks for y axis, default is 5
 * - yAxisValues - tick values for y axis, if provided yAxisNumber will be disregarded
 * - valuePoints - if true, shows a dot on parts of graph that have values
 * - tooltip - boolean value whether to display tooltip on hover or not
 * - highlightedGraph - real time highlighting graph stroke based on which value is assigned
 * - uniqueId - String representing unique identifier so that graphs are independent
 */


require('./ctxGraph.style.scss');

class GraphController {
    constructor($scope, $element, $window, $timeout, $attrs, $state) {
        this.$scope = $scope;
        this.$element = $element;
        this.$window = $window;
        this.$timeout = $timeout;
        this.$attrs = $attrs;
        this.$state = $state;
    }

    $onInit() {
        this.initialDataSetWatcher = this.$scope.$watch(() => this.data, (newVal, oldVal) => {
            if (!angular.isUndefined(newVal) || !angular.isUndefined(oldVal)) {
                this.$timeout(() => {
                    this.drawGraph(this.data);
                });
            }
        });
        if (angular.isUndefined(this.$attrs.dimensionWatch)) return;
        this.$scope.$watch(() => angular.element(this.$attrs.dimensionWatch).width() || angular.element(this.$attrs.dimensionWatch).height(), () => {
            this.$timeout(() => {
                this.drawGraph(this.data);
            }, 250);
        });
    }

    drawGraph(data) {
        this.width = this.$element.find(".graph-graph").width();
        this.height = this.$element.find(".graph-graph").height();
        this.$element.find(".graph-graph").empty();
        if (angular.isUndefined(data)) {
            return;
        }
        let start = this.startTime || Date.now(),
            paths = [],
            strokes = [],
            dots = [],
            valueUnit = this.valueUnit || "",
            maxY = this.maxValue || this.calculateMax(this.data),
            period = this.period || this.calculatePeriod(this.data),
            periodSet = Boolean(this.period),
            xDomain = this.period ? [start - period, start] : this.calculateDomain(this.data),
            yDomain = [0, maxY],
            timeUnit = this.timeUnit,
            timeStep = this.timeStep,
            timeFormat = this.timeFormat,
            tickNumber = this.yAxisNumber || 5,
            tickValues = this.yAxisValues,
            width = this.width,
            height = this.height,
            margin = { top: this.height * 0.02, right: this.width * (this.$attrs.dimensionWatch === '.details-view' ? 0.04 : 0.08), bottom: this.height * 0.1, left: this.fixLeft ? this.fixLeft : (this.width * 0.08) },
            tooltipAttribute = angular.isUndefined(this.tooltipAttribute) ? 'y' : this.tooltipAttribute;

        let x = this.$window.d3.time.scale()
            .domain(xDomain)
            .range([0, width - margin.left - margin.right]);

        let y = this.$window.d3.scale.linear()
            .domain(yDomain)
            .range([height - margin.top - margin.bottom, 0]);

        let make_y_axis = () => {
            return d3.svg.axis()
                .scale(y)
                .orient("left")
                .ticks(tickNumber)
                .tickValues(tickValues).tickFormat(d => {
                    if (maxY == 100) {
                        return d + "%";
                    }
                    return d;
                });
        };


        let make_x_axis = () => {
            let axis = d3.svg.axis()
                .scale(x)
                .orient("bottom")
                .ticks(d3.time[timeUnit], timeStep);
            if (timeFormat) axis.tickFormat(d3.time.format(timeFormat));
            else if (this.hoursPerDay) {
                let timeFormatWithDate = d3.time.format("%b %d"),
                    timeFormatWithoutDate = d3.time.format("%I %p"),
                    temp;
                axis.tickFormat(function (d, i) {
                    if (temp !== d.getDay()) {
                        temp = d.getDay();
                        return timeFormatWithDate(d);
                    }
                    else
                        return timeFormatWithoutDate(d);
                });
            }
            return axis;
        };

        let area = this.$window.d3.svg.area()
            .interpolate('linear')
            .x(function (d, i) { return x(d.x); })
            .y0(height)
            .y1(function (d) { return y(d.y); });

        let line = this.$window.d3.svg.line()
            .interpolate('linear')
            .x(function (d) { return x(d.x); })
            .y(function (d) { return y(d.y); });

        let placeholder = this.$element.find(".graph-graph")[0];
        let svg = this.$window.d3.select(placeholder)
            .append("svg")
            .attr("viewBox", "0, 0," + this.width + "," + this.height)
            .attr("preserveAspectRatio", "none meet")
            .attr("width", this.width)
            .attr("height", this.height);

        svg.append('defs')
            .append("clipPath")
            .attr("id", 'chartFrame' + this.uniqueId)
            .append("rect")
            .attr("x", 0)
            .attr("y", 0)
            .attr("width", width - margin.right - margin.left)
            .attr("height", height - margin.top - margin.bottom);

        svg.append('defs')
            .append("clipPath")
            .attr("id", 'dotsFrame' + this.uniqueId)
            .append("rect")
            .attr("x", -4)
            .attr("y", -6)
            .attr("width", width - margin.right - margin.left + 8)
            .attr("height", height - margin.top - margin.bottom + 8);

        let yGrid = svg.append("g")
            .attr("class", "grid")
            .attr("transform", "translate(" + margin.left + "," + margin.top + ")")
            .call(make_y_axis()
                .tickSize(-width + margin.left + margin.right, 0, 0)
                .tickPadding(12)
                //.tickFormat("")
            );



        let xGrid = svg.append("g")
            .attr("class", "grid")
            .attr("transform", "translate(" + margin.left + "," + (height - margin.bottom) + ")")
            .call(make_x_axis()
                .tickSize(-height + margin.top + margin.bottom, 0, 0)
                .tickPadding(12)
                // .tickFormat("")
            );
        if (this.$attrs.dimensionWatch === '.details-view') {
            this.$element.find('.grid:even').find('text:odd').css('display', 'none');
            if (this.width <= 427) {
                this.$element.find('.grid:odd').find('text:odd').css('display', 'none');
            }
        }

        for (let i = Object.keys(this.data).length - 1; i >= 0; i--) {

            if (this.areaChart[i]) {
                paths[i] = svg.append("path")
                    .datum(this.data['graph' + (i + 1)])
                    .style('fill', this.colors[i].fillColor)
                    .attr("d", area)
                    .attr("graph" + (i + 1), '')
                    .style("clip-path", "url(#chartFrame" + this.uniqueId + ")")
                    .attr("transform", "translate(" + margin.left + "," + margin.top + ")");
            }

            strokes[i] = svg.append("path")
                .datum(this.data['graph' + (i + 1)])
                .attr("class", 'stroke')
                .style('stroke', this.colors[i].strokeColor)
                .attr("d", line)
                .attr("graph" + (i + 1), '')
                .style("clip-path", "url(#chartFrame" + this.uniqueId + ")")
                .attr("transform", "translate(" + margin.left + "," + margin.top + ")")
                .attr("fill", "none");

            if (this.valuePoints && this.valuePoints !== 'false') {
                dots[i] = svg.append("g")
                    .attr("graph" + (i + 1), '')
                    .attr("transform", "translate(" + margin.left + "," + margin.top + ")")
                    .selectAll("circle")
                    .data(this.data['graph' + (i + 1)]);
                dots[i]
                    .enter()
                    .append("circle")
                    .attr("point-value", true)
                    .style("clip-path", "url(#dotsFrame" + this.uniqueId + ")")
                    .attr("fill", this.colors[i].strokeColor)
                    .attr("r", 3)
                    .attr("cx", d => x(d.x))
                    .attr("cy", d => y(d.y));
            }
        }

        if (this.tooltip && this.tooltip !== 'false') {
            let tooltip = this.createTooltip(svg, margin, !(this.valuePoints && this.valuePoints !== 'false'));

            if (this.valuePoints && this.valuePoints !== 'false') {
                svg.selectAll("circle[point-value=true]")
                    .on("mouseover", (d) => {
                        tooltip.html(d[tooltipAttribute] + (valueUnit ? valueUnit : ''));
                        this.$timeout(() => {
                            tooltip.style("top", y(d.y) + margin.top - $(tooltip[0]).outerHeight() - 12 + 'px');
                            tooltip.style("left", x(d.x) + margin.left - $(tooltip[0]).outerWidth() / 2 + 'px');
                            tooltip.style("display", null);
                        });
                    })
                    .on("mouseout", function (d) {
                        tooltip.style("display", "none");
                    });

                if (this.circleClick) {
                    svg.selectAll("circle[point-value=true]").on('click', (d) => {
                        let widgetId = this.uniqueId.split('-');
                        let eightHoursMS = 1000 * 60 * 60 * 8;
                        let data = {
                            statusFilter: d.status,
                            created: JSON.stringify({
                                AFR: d.x - eightHoursMS,
                                BFR: d.x,
                            })
                        };
                        this.$timeout(() => {
                            this.$state.go(widgetId[1], data);
                        }, 0);
                    });
                }
            }
            else {
                let focus = svg.append("g")
                    .attr("class", "focus")
                    .attr("tooltip", '')
                    .style("display", "none");

                focus.append("circle")
                    .attr("r", 4.5)
                    .attr("transform", "translate(" + margin.left + "," + margin.top + ")");

                svg.append("rect")
                    .attr("class", "overlay")
                    .attr("transform", "translate(" + margin.left + "," + margin.top + ")")
                    .attr("width", width - margin.left - margin.right)
                    .attr("height", height - margin.top - margin.bottom)
                    .on("mouseover", function () {
                        tooltip.style("display", null);
                        focus.style("display", null);
                    })
                    .on("mouseout", function () {
                        tooltip.style("display", "none");
                        focus.style("display", "none");
                    })
                    .on("mousemove", mousemove);

                let that = this;

                function mousemove() {
                    let bisectDate = that.$window.d3.bisector(function (d) {
                        return d.x;
                    }).left;
                    let x0 = x.invert(that.$window.d3.mouse(this)[0]),
                        i = bisectDate(that.data.graph1, x0, 1),
                        d0 = that.data.graph1[i - 1],
                        overflow = i > d0.x ? d0.x : i,
                        d1 = that.data.graph1[overflow],
                        d = d1 ? (x0 - d0.x > d1.x - x0 ? d1 : d0) : d0;

                    focus.attr("transform", "translate(" + x(d.x) + "," + y(d.y) + ")");

                    tooltip.html(d[tooltipAttribute] + (valueUnit ? " " + valueUnit : ''));
                    tooltip.style("top", y(d.y) + margin.top - $(tooltip[0]).outerHeight() - 12 + 'px');
                    tooltip.style("left", x(d.x) + margin.left - $(tooltip[0]).outerWidth() / 2 + 'px');
                }
            }
        }

        if (this.timeTick && this.timeTick !== 'false') {
            let timeGap = 0,
                timePerPixel = period / width;
            if (!periodSet) {
                timeGap = Date.now() - xDomain[1];
            }

            let tick = () => {
                //TODO: implement mousover time movement
                let latest = Date.now() - timeGap;

                Object.keys(this.data).forEach((key) => {
                    if (this.data[key][1] && this.data[key][0].x < latest - period - (this.data[key][1].x - this.data[key][0].x)) {
                        this.data[key].shift();
                    }
                });

                x.domain([latest - period, latest]);
                xGrid
                    .call(make_x_axis()
                        .tickSize(-height + margin.top + margin.bottom, 0, 0)
                    )
                    .transition();
                // .each('end', tick);

                d3.timer(tick, timePerPixel);

                if (paths.length !== 0) {
                    paths.forEach((path) => {
                        path.attr("d", area)
                            .transition();
                    });
                }

                strokes.forEach((stroke) => {
                    stroke
                        .attr("d", line)
                        .transition();
                });

                if (this.valuePoints) {
                    for (let i = 0; i < dots.length; i++) {
                        dots[i].data(this.data['graph' + (i + 1)])
                            .attr("cx", d => x(d.x))
                            .attr("cy", d => y(d.y));
                    }
                }

                return true;

            };

            tick();
        }

        this.$scope.$watch(() => this.highlightedGraph, (newVal, oldValue) => {
            if (!angular.isUndefined(newVal)) {
                let graphComponents = svg.selectAll('[graph' + (newVal + 1) + "]");
                graphComponents[0].forEach(graphComponent => {
                    svg.node().appendChild(graphComponent);
                });
                if (svg.selectAll("[tooltip]")[0][0]) svg.node().appendChild(svg.selectAll("[tooltip]")[0][0]);
                if (svg.selectAll("rect.overlay")[0][0]) svg.node().appendChild(svg.selectAll("rect.overlay")[0][0]);
                strokes[newVal].style("stroke-width", "3px");
            }
            else if (!angular.isUndefined(oldValue)) {
                strokes[oldValue].style("stroke-width", null);
            }

        });
    }

    createTooltip() {

        let placeholder = this.$element.find(".graph-graph")[0];
        return this.$window.d3.select(placeholder)
            .append('div')
            .attr('class', 'graph-tooltip')
            .style("display", "none");

    }

    calculateMax(data) {
        let res = 0;
        Object.keys(data).forEach((key) => {
            let temp = this.$window.d3.max(this.data[key], function (d) { return d.y; });
            if (temp > res) res = temp;
        });
        return res;
    }

    calculatePeriod(data) {
        let res = this.calculateDomain(data);
        return res[1] - res[0];
    }

    calculateDomain(data) {
        let start = data.graph1[0].x,
            end = 0;
        Object.keys(data).forEach((key) => {
            if (data[key][0].x < start) {
                start = data[key][0].x;
            }
            if (data[key][data[key].length - 1].x > end) {
                end = data[key][data[key].length - 1].x;
            }
        });
        return [start, end];
    }
}

const graphComponent = {
    controller: GraphController,
    require: ['ngModel'],
    scope: {},
    template: require('./ctxGraph.template.html'),
    bindings: {
        data: '=',
        areaChart: '<',
        colors: '<',
        timeStep: '@',
        timeUnit: '@',
        timeFormat: '@',
        hoursPerDay: '@?',
        valueUnit: '@',
        maxValue: '@?',
        period: '@',
        startTime: '@',
        graphTitle: '@',
        timeTick: '@',
        yAxisNumber: '@',
        yAxisValues: '<',
        valuePoints: '@',
        tooltip: '@',
        tooltipAttribute: '@',
        highlightedGraph: '<',
        fixLeft: '<?',
        uniqueId: '@',
        circleClick: '<?'
    }
};

GraphController.$inject = ['$scope', '$element', '$window', '$timeout', '$attrs', '$state'];

module.exports = graphComponent;



// WEBPACK FOOTER //
// ./src/shared/ctxGraph/ctxGraph.component.js